import {
  ComponentPropsWithoutRef,
  forwardRef,
  useRef,
  useEffect,
  KeyboardEvent,
  useId,
  useCallback,
  useState,
} from "react";
import cx from "classnames";
import { useMergeRefs, useOnClickOutside } from "../../hooks";
import { useDialogScrollLock } from "../../hooks/useDialogScrollLock";
import styles from "./Dialog.module.scss";
import { DisableCloseOnEscapeOrClickProps, Size } from "../../types";
import { DialogHeader } from "./DialogHeader";
import { DialogFooter } from "./DialogFooter";
import { DialogContent } from "./DialogContent";
import { DialogCancelButton } from "./DialogCancelButton";
import { useKeyboardFocusables } from "../../hooks";
import { ToastProvider } from "../Toast/ToastProvider";
import { ToasterContent } from "../Toast/Toaster";
import { ToasterPopover } from "../Toast/ToasterPopover";
import { getActiveElement } from "../../internal";
import { DialogContext, DialogContextProps } from "./utils/DialogContext";
import { useDialogTransitionStates } from "../../hooks/useDialogTransitionStates";
import { OpenCloseTransitionStateEffects } from "../../hooks/useOpenCloseTransitionStates";

/**
 * Props for the Dialog component
 * @extends ComponentPropsWithoutRef<"dialog">
 * @extends DialogTransitionStateEffects
 * @extends DisableCloseOnEscapeOrClickProps
 */
export type DialogProps = ComponentPropsWithoutRef<"dialog"> &
  OpenCloseTransitionStateEffects &
  DisableCloseOnEscapeOrClickProps & {
    /**
     * @default false
     */
    fullScreen?: boolean;
    /**
     * @default "medium"
     */
    size?: Extract<Size, "medium" | "large">;

    /**
     * @default false
     */
    open?: boolean;
    onClose?: () => void;
    /**
     * @deprecated - use onOpenAnimationStart or onOpenAnimationComplete instead
     */
    onOpen?: () => void;
    /**
     * @deprecated - use onOpenAnimationStart or onOpenAnimationComplete instead
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * Callback when clicking outside the dialog
     */
    onClickOutside?: (e: globalThis.MouseEvent) => void;
    /**
     * Enables scroll chaining
     * @link https://developer.mozilla.org/en-US/docs/Glossary/Scroll_chaining
     */
    enableScrollChaining?: boolean;
  };

export const DialogElement = forwardRef<HTMLDialogElement, DialogProps>(
  (props, ref) => {
    const {
      className,
      open = false,
      children,
      onOpen,
      onOpenChange,
      onClose,
      onKeyDown,
      fullScreen = false,
      disableCloseOnEscape = false,
      onClickOutside,
      disableCloseOnClickOutside = false,
      size,
      enableScrollChaining,
      onOpenAnimationStart,
      onOpenAnimationComplete,
      onCloseAnimationStart,
      onCloseAnimationComplete,
      ...rest
    } = props;

    const elRef = useRef<HTMLDialogElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const toasterRef = useRef<HTMLDivElement>(null);
    const [hasOpenPopover, setHasOpenPopover] =
      useState<DialogContextProps["hasOpenPopover"]>(false);
    const id = useId();

    const { state, isOpenOrOpening } = useDialogTransitionStates(elRef, open, {
      onOpenAnimationStart: useCallback(() => {
        onOpenAnimationStart?.();
      }, [onOpenAnimationStart]),
      onOpenAnimationComplete: useCallback(() => {
        onOpenAnimationComplete?.();
        onOpen?.(); // DEPRECATED
      }, [onOpen, onOpenAnimationComplete]),
      onCloseAnimationStart: useCallback(() => {
        onCloseAnimationStart?.();
      }, [onCloseAnimationStart]),
      onCloseAnimationComplete: useCallback(() => {
        onCloseAnimationComplete?.();
      }, [onCloseAnimationComplete]),
    });

    // DEPRECATED
    const initial = useRef(true);
    useEffect(() => {
      if (initial.current) {
        initial.current = false;
      } else {
        onOpenChange?.(isOpenOrOpening);
      }
    }, [id, onOpenChange, isOpenOrOpening]);

    const classNames = cx([styles["dialog"]], className, {
      [styles["closing"]]: state === "closing",
      [styles["fullscreen"]]: fullScreen,
      [styles["large"]]: size === "large",
    });

    const { focusables } = useKeyboardFocusables(contentRef, {
      observeChange: true,
    });

    useDialogScrollLock({
      open: isOpenOrOpening,
      id,
      enableScrollChaining,
      elementRef: elRef,
    });

    useOnClickOutside({
      onOutsidePress: (e) => {
        if (!open) return;
        onClickOutside?.(e);
      },
      targets: [
        contentRef.current as HTMLElement,
        toasterRef.current as HTMLElement,
      ],
      onClose: () => {
        if (disableCloseOnClickOutside || !open) return;
        onClose?.();
      },
    });

    useEffect(() => {
      if (!isOpenOrOpening) return;
      if (!focusables) return;

      const activeElement = getActiveElement(document);
      if (!activeElement) return;

      if (
        activeElement.dataset.anv !== "drawer-header-close-button" &&
        (focusables.includes(activeElement) ||
          activeElement.tagName === "DIALOG")
      ) {
        return;
      }

      if (
        focusables[0]?.dataset.anv === "dialog-header-close-button" &&
        focusables[1]
      ) {
        focusables[1].focus();
      } else {
        focusables[0]?.focus();
      }
    }, [isOpenOrOpening, focusables]);

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDialogElement>) => {
      onKeyDown?.(e);
      if (e.code === "Escape") {
        e.preventDefault();
        if (disableCloseOnEscape) return;
        if (hasOpenPopover) return;
        onClose?.();
        return;
      }

      if (e.code === "Tab") {
        if (e.shiftKey && e.target === focusables?.[0]) {
          e.preventDefault();
          (focusables[focusables.length - 1] as HTMLElement)?.focus();
          return;
        }
        if (e.target === focusables?.[focusables.length - 1] && !e.shiftKey) {
          e.preventDefault();
          focusables[0].focus();
          return;
        }
      }
    };

    return (
      // dialog element does seem to be interactive on MDN guideline
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <dialog
        className={classNames}
        data-anv="dialog"
        data-anv-scroll-chaining={enableScrollChaining}
        data-anv-transition-state={state}
        ref={useMergeRefs([ref, elRef])}
        onKeyDown={onKeyDownHandler}
        {...rest}
      >
        <ToastProvider isDialogToaster={open}>
          <ToasterPopover>
            <ToasterContent id="dialog-toaster" ref={toasterRef} />
          </ToasterPopover>
        </ToastProvider>
        <DialogContext.Provider
          value={{
            close: () => onClose?.(),
            ref: elRef,
            hasOpenPopover,
            setHasOpenPopover,
          }}
        >
          <section
            ref={contentRef}
            className={styles["container"]}
            data-open={open}
          >
            {children}
          </section>
        </DialogContext.Provider>
      </dialog>
    );
  },
);

DialogElement.displayName = "Dialog";

/**
 * Dialog component for creating modal dialogs and overlays.
 *
 * Features:
 * - Modal dialog with backdrop overlay
 * - Controlled and uncontrolled open state
 * - Smooth open/close animations
 * - Keyboard navigation and focus management
 * - Escape key and click outside to close
 * - Multiple size variants (medium, large, fullscreen)
 * - Scroll chaining support
 * - Toast integration for notifications
 * - Compound component pattern with Header, Footer, Content, and CancelButton sub-components
 *
 * @example
 * <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
 *   <Dialog.Header>Confirm Action</Dialog.Header>
 *   <Dialog.Content>Are you sure you want to proceed?</Dialog.Content>
 *   <Dialog.Footer>
 *     <Dialog.CancelButton>Cancel</Dialog.CancelButton>
 *     <Button appearance="primary">Confirm</Button>
 *   </Dialog.Footer>
 * </Dialog>
 */
export const Dialog = Object.assign(DialogElement, {
  /**
   * DialogHeader component for the header section of a Dialog.
   *
   * Features:
   * - Displays dialog title with headline typography
   * - Includes close button with proper accessibility
   * - Sticky positioning for long content
   * - Consistent spacing and styling
   * - Automatic focus management
   *
   * @example
   * <Dialog.Header>Confirm Delete</Dialog.Header>
   */
  Header: DialogHeader,
  /**
   * DialogFooter component for the footer section of a Dialog.
   *
   * Features:
   * - Container for dialog action buttons
   * - Optional sticky positioning for long content
   * - Consistent spacing and alignment
   * - Right-aligned button layout by default
   * - Flexible content area for custom layouts
   *
   * @example
   * <Dialog.Footer sticky>
   *   <Dialog.CancelButton>Cancel</Dialog.CancelButton>
   *   <Button appearance="primary">Save</Button>
   * </Dialog.Footer>
   */
  Footer: DialogFooter,
  /**
   * DialogContent component for the main content area of a Dialog.
   *
   * Features:
   * - Main content container for dialog body
   * - Flexible height with overflow handling
   * - Consistent padding and spacing
   * - Responsive design
   * - Proper content scrolling for long content
   *
   * @example
   * <Dialog.Content>
   *   <p>This is the main content of the dialog.</p>
   * </Dialog.Content>
   */
  Content: DialogContent,
  /**
   * DialogCancelButton component for cancel/close actions in a Dialog.
   *
   * Features:
   * - Button that automatically closes the dialog when clicked
   * - Extends all Button component props and functionality
   * - Proper accessibility with close dialog semantics
   * - Automatic focus management
   * - Consistent styling with other dialog elements
   *
   * @example
   * <Dialog.CancelButton appearance="ghost">Cancel</Dialog.CancelButton>
   */
  CancelButton: DialogCancelButton,
});
